/**
 * GlobalSearchBar — global member search with debounced query and dropdown results.
 *
 * Typing 2+ characters triggers a search against /api/members?search=...
 * On API unavailability (local dev, demo kiosk), falls back to filtering
 * MOCK_MEMBERS in memory.
 *
 * Selecting a result navigates to /members/:memberId (Member 360 profile).
 * Click-outside and Escape key close the dropdown.
 *
 * Usage:
 *   <GlobalSearchBar /> — drop into the top navigation bar
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { MOCK_MEMBERS, MemberListItem } from '../lib/mock-members';

export function GlobalSearchBar(): React.ReactElement {
  const navigate = useNavigate();

  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<MemberListItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  // ---- Debounce rawQuery → debouncedQuery (300 ms) ----
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(rawQuery), 300);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  // ---- Execute search when debouncedQuery changes ----
  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setIsSearching(true);
      try {
        const token = localStorage.getItem('crm_dev_token') ?? '';
        const res = await fetch(
          `/api/members?search=${encodeURIComponent(debouncedQuery)}&limit=9`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { members: MemberListItem[] };
        if (!cancelled) {
          setResults(data.members.slice(0, 9));
          setIsOpen(true);
        }
      } catch {
        // API unavailable — fall back to in-memory mock filter
        if (!cancelled) {
          const q = debouncedQuery.toLowerCase();
          const filtered = MOCK_MEMBERS.filter(
            (m) =>
              m.displayName.toLowerCase().includes(q) ||
              m.memberNumber.includes(q),
          ).slice(0, 9);
          setResults(filtered);
          setIsOpen(true);
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };

    void run();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  // ---- Close dropdown on click outside ----
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ---- Navigate to member 360 on result selection ----
  const selectMember = (memberId: string) => {
    setIsOpen(false);
    setRawQuery('');
    navigate('/members/' + memberId);
  };

  return (
    <div className="relative" ref={panelRef}>
      <label className="sr-only" htmlFor="global-search">Search members</label>

      {/* Search icon */}
      <Search className="h-4 w-4 text-ink-muted absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />

      {/* Input */}
      <input
        id="global-search"
        type="search"
        placeholder="Search members..."
        value={rawQuery}
        onChange={(e) => setRawQuery(e.target.value)}
        onFocus={() => { if (results.length > 0) setIsOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
        className="w-56 focus:w-72 transition-all pl-9 pr-3 py-2 text-sm rounded-lg border border-line-strong focus:ring-2 focus:ring-brand focus:border-transparent bg-white"
      />

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-lg border border-line bg-white shadow-lg z-50">
          {isSearching ? (
            <div className="px-4 py-3 text-sm text-ink-muted">Searching...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-ink-muted italic">No members found</div>
          ) : (
            <>
              {results.slice(0, 8).map((m) => (
                <button
                  key={m.memberId}
                  onClick={() => selectMember(m.memberId)}
                  className="w-full px-4 py-2.5 text-sm text-ink hover:bg-bg border-b border-sunken last:border-0 text-left flex items-center justify-between"
                >
                  <span className="font-medium">{m.displayName}</span>
                  <span className="text-xs text-ink-muted">#{m.memberNumber}</span>
                </button>
              ))}
              <button
                onClick={() => { setIsOpen(false); navigate('/members'); }}
                className="w-full px-4 py-2 text-xs text-brand-dark hover:underline border-t border-sunken text-left"
              >
                View all in Members
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
